import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../components/ui'
import type { ValueNode } from '../funnel/flow'
import { useFunnel } from '../funnel/useFunnel'
import { eventOnce, initialSessionId } from '../lib/api'

/**
 * The first payoff screen: their stated goal, drawn back as a question-balance
 * chart. This is the earliest point where the funnel gives something instead of
 * only taking, which is what buys the second half of the questions.
 *
 * It's an explicit *recommendation from their choices*, never a score — the
 * note under the chart says so, and the copy guardrails forbid the alternative.
 */
const GOAL_BARS: Record<string, Array<{ key: 'playful' | 'warm' | 'deep' | 'bold'; value: number }>> = {
  fun: [
    { key: 'playful', value: 100 },
    { key: 'warm', value: 62 },
    { key: 'deep', value: 34 },
  ],
  closeness: [
    { key: 'warm', value: 100 },
    { key: 'deep', value: 74 },
    { key: 'playful', value: 46 },
  ],
  understanding: [
    { key: 'warm', value: 96 },
    { key: 'playful', value: 76 },
    { key: 'deep', value: 30 },
  ],
  growth: [
    { key: 'deep', value: 100 },
    { key: 'warm', value: 68 },
    { key: 'playful', value: 38 },
  ],
}

export function InsightScreen({ node }: { node: ValueNode }) {
  const { t } = useTranslation()
  const { answers, advance } = useFunnel()

  const goal = typeof answers.connection_goal === 'string' ? answers.connection_goal : 'closeness'
  const bars = useMemo(() => GOAL_BARS[goal] ?? GOAL_BARS.closeness, [goal])

  useEffect(() => {
    void eventOnce(initialSessionId, 'visual_insight_viewed', { goal }).catch(() => {})
  }, [goal])

  return (
    <div className="content-column text-center">
      <p className="eyebrow">{t('insight.eyebrow')}</p>
      <h1 className="value-title">{t(`insight.goals.${goal}.title`)}</h1>
      <p className="body-copy">{t(`insight.goals.${goal}.body`)}</p>

      <figure className="insight-chart">
        <figcaption className="chart-label">{t('insight.chartLabel')}</figcaption>
        {bars.map((bar, index) => (
          <div className="chart-row" key={bar.key}>
            <span className="chart-key">{t(`insight.labels.${bar.key}`)}</span>
            <span className="chart-track">
              <span
                className="chart-fill"
                // Staggered so the bars grow in sequence rather than all at once.
                style={{ width: `${bar.value}%`, animationDelay: `${index * 120}ms` }}
              />
            </span>
          </div>
        ))}
      </figure>

      <p className="fine-print">{t('insight.note')}</p>
      <Button block size="lg" variant="sunshine" onClick={() => advance(node.id)}>
        {t('insight.cta')}
      </Button>
    </div>
  )
}
